/**
 * The words a translation keeps exactly as written: the product's name, and
 * the names a script mentions — other products, models, acronyms, a law's
 * name. Translated, "Claude" becomes a first name and "GDPR" an acronym
 * nobody in that country uses; kept, they are what the viewer searches for.
 *
 * Found rather than asked for, so a first translation keeps them without the
 * customer doing anything. The customer's own list, once they edit it,
 * replaces this one (`Project.keepTerms`).
 */

/** Capitalised words that are only capitalised because they start a sentence or are everyday words. */
const COMMON = new Set(
  "A An And As At Be But By For From How I If In Into It Its Let Make No Not Now Of On One Or Our So Start Still Than That The Their Them Then There These They This To Up We What When Where Which While Who Why With You Your Yours Just Every Each All Any Some Get Set Choose Bring Share Built Hosted Research Tools Knowledge Central Confidential Safely First Second Third Next Finally Plus Also Here Today Tomorrow Yes Meet Try See Find Save Send Ship Connect Open Join".split(
    " ",
  ),
);

/**
 * Capitalised words a language has its own word for: days, months, the
 * continents and the places a launch script tends to name. "Monday" and
 * "Europe" are translated, never kept.
 */
const TRANSLATED = new Set(
  "Monday Tuesday Wednesday Thursday Friday Saturday Sunday January February March April May June July August September October November December Europe European Asia Asian Africa African America American Americas Australia Germany German France French Spain Spanish Italy Italian Japan Japanese China Chinese India Indian Brazil Britain British England English Arabic Turkey Turkish Korea Korean Netherlands Dutch".split(
    " ",
  ),
);

/** A name that starts lower case: a brand (iPhone, eBay, macOS) or an address (acme.com). */
const LOWER_NAME = /^\p{Ll}+\p{Lu}[\p{L}\p{N}]*$|^[\p{Ll}\p{N}-]+\.(?:com|io|ai|app|dev|co|org|net)$/u;

const WORD = /[\p{L}\p{N}][\p{L}\p{N}'’-]*(?:\.(?:com|io|ai|app|dev|co|org|net)\b)?/gu;

/** Whether a word is a name: an acronym, a mixed-case brand (ChatGPT), or a capitalised word not in the common list. */
function isName(word: string): boolean {
  if (TRANSLATED.has(word)) return false;
  if (LOWER_NAME.test(word)) return true; // iPhone, macOS, acme.com
  if (/^\p{Lu}{2,}\d*$/u.test(word)) return true; // GDPR, EU, AI
  if (/^\p{Lu}/u.test(word) && /\p{Lu}/u.test(word.slice(1))) return true; // ChatGPT, OpenAI
  return /^\p{Lu}\p{Ll}/u.test(word) && !COMMON.has(word);
}

export function detectKeepTerms(script: string, productName: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (term: string) => {
    const clean = term.trim();
    if (!clean || seen.has(clean.toLowerCase())) return;
    seen.add(clean.toLowerCase());
    out.push(clean);
  };
  add(productName);

  // After a full stop the first word is capitalised anyway; after a colon,
  // a semicolon or a dash it is capitalised only if it is a name.
  const clauses = script
    .split(/(?<=[.!?…])\s+|\n+/)
    .flatMap((sentence) => sentence.split(/(?<=[:;])\s+|\s+[—–]\s+/).map((text, index) => ({ text, soft: index > 0 })));
  // The names written capitalised mid-sentence somewhere: a word that only
  // starts a clause after a colon is a name if it is one of these.
  const midNames = new Set(
    clauses.flatMap(({ text }) =>
      [...text.matchAll(WORD)].slice(1).flatMap((match) => (isName(match[0]) ? [match[0]] : [])),
    ),
  );
  for (const { text: sentence, soft } of clauses) {
    const words = [...sentence.matchAll(WORD)].map((match) => ({ text: match[0], at: match.index ?? 0 }));
    let run: string[] = [];
    let runEnd = -1;
    const flush = () => {
      if (run.length > 0) add(run.join(" "));
      run = [];
    };
    words.forEach((word, index) => {
      // The first word of a sentence is capitalised anyway: a name only when it
      // is an acronym or mixed case, or starts a list of names ("Mistral, Claude").
      const following = words[index + 1];
      const startsList =
        following !== undefined &&
        /^,\s*$/.test(sentence.slice(word.at + word.text.length, following.at)) &&
        isName(following.text);
      const name =
        index === 0 && soft
          ? isName(word.text) &&
            (!/^\p{Lu}\p{Ll}/u.test(word.text) ||
              midNames.has(word.text) ||
              (following !== undefined &&
                /^\s+$/.test(sentence.slice(word.at + word.text.length, following.at)) &&
                isName(following.text)))
          : index === 0
            ? LOWER_NAME.test(word.text) ||
              /^\p{Lu}{2,}\d*$/u.test(word.text) ||
              (/^\p{Lu}/u.test(word.text) && /\p{Lu}/u.test(word.text.slice(1))) ||
              (startsList && /^\p{Lu}/u.test(word.text) && !COMMON.has(word.text) && !TRANSLATED.has(word.text))
            : isName(word.text);
      const adjacent = run.length > 0 && /^\s+$/.test(sentence.slice(runEnd, word.at));
      if (name && (run.length === 0 || adjacent)) {
        run.push(word.text);
        runEnd = word.at + word.text.length;
      } else {
        flush();
        if (name) {
          run.push(word.text);
          runEnd = word.at + word.text.length;
        }
      }
    });
    flush();
  }
  // A term that is the product's name with more after it ("Zentre AI Pro") stays; a whole word of it does not.
  const product = productName.trim().toLowerCase();
  const productWords = ` ${product.split(/\s+/).join(" ")} `;
  return out.filter((term) => term.toLowerCase() === product || !productWords.includes(` ${term.toLowerCase()} `));
}

/** The list a translation uses: the customer's, when they made one, else the found one. */
export function keepTermsFor(saved: readonly string[] | null, script: string, productName: string): string[] {
  const list = saved ?? detectKeepTerms(script, productName);
  const product = productName.trim();
  return [...new Set([product, ...list].map((one) => one.trim()).filter(Boolean))];
}
